import React, { ChangeEvent, FC } from 'react';
import { FormControlLabel, Radio } from '@mui/material';
import { SubmitHandler, useFormContext } from 'react-hook-form';
import { Button } from 'src/components/Button';
import {
  Form,
  InnerWrapper,
  InputsWrapper,
  RadioButtonsWrapper,
  Title,
} from 'src/components/Forms/CompanyForm/styled';
import { TextInput } from 'src/components/Forms/FormInputs/TextInput';
import { UsageOption } from 'src/utils/gql/types';
import { useIsAdmin } from 'src/hooks/useIsAdmin';

export interface CompanyFormValues {
  usageOptions: UsageOption;
  companyDescription: string;
  name: string;
  industry: string;
}

const usageOptions = [
  { label: 'Improve sales', id: UsageOption.ImproveSales },
  { label: 'Improve Company Margin', id: UsageOption.ImproveCompanyMargin },
  {
    label: 'Optimize cost of advertising',
    id: UsageOption.ReduceCostOfAdvertising,
  },
  { label: 'I don’t know yet', id: UsageOption.Unknown },
];

interface CompanyFormProps {
  onSubmit: SubmitHandler<CompanyFormValues>;
  buttonLoading: boolean;
  inputLoading?: boolean;
  isUpdateForm?: boolean;
  isRegistrationForm?: boolean;
}

export const CompanyForm: FC<CompanyFormProps> = ({
  onSubmit,
  buttonLoading,
  inputLoading,
  isUpdateForm,
  isRegistrationForm = false,
}) => {
  const {
    formState: { errors, isValid },
    register,
    setValue,
    handleSubmit,
    watch,
  } = useFormContext<CompanyFormValues>();

  const isAdmin = useIsAdmin();

  // Durante la registrazione, i campi devono essere sempre modificabili
  const canEditFields = isAdmin || isRegistrationForm;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setValue(name as keyof CompanyFormValues, value, { shouldValidate: true });
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <InputsWrapper>
        <InnerWrapper flexDirection={isUpdateForm ? 'row' : 'column'}>
          <TextInput
            label="Company name"
            name="name"
            type="text"
            error={!!errors.name}
            helperText={errors.name?.message || ' '}
            loading={inputLoading}
            disabled={!canEditFields}
          />
          <TextInput
            label="Industry"
            name="industry"
            type="text"
            error={!!errors.industry}
            helperText={errors.industry?.message || ' '}
            loading={inputLoading}
            disabled={!canEditFields}
          />
        </InnerWrapper>
        <TextInput
          multiline
          label="Company description"
          name="companyDescription"
          type="text"
          error={!!errors.companyDescription}
          helperText={
            errors.companyDescription?.message ||
            'Please provide a detailed description of your company to ensure the app delivers the most tailored responses.'
          }
          loading={inputLoading}
          disabled={!canEditFields}
        />
      </InputsWrapper>
      <Title variant="body2">How you will use Kipify?</Title>
      <RadioButtonsWrapper>
        {usageOptions.map(({ id, label }) => (
          <FormControlLabel
            key={id}
            {...register('usageOptions')}
            control={
              <Radio
                checked={watch('usageOptions') === id}
                onChange={handleChange}
                disabled={!canEditFields}
              />
            }
            label={label}
            value={id}
          />
        ))}
      </RadioButtonsWrapper>
      {canEditFields && (
        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={!isValid}
          loading={buttonLoading}
        >
          {isUpdateForm ? 'Save' : 'Submit'}
        </Button>
      )}
    </Form>
  );
};
